import type { ComponentType } from 'react';

import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { HeadingProps } from '@atlaskit/heading/heading';
import type { IconTileProps, NewCoreIconProps } from '@atlaskit/icon/types';
import type {
	RovoSpaceEntryPoint,
	SmartLink3PPostAuthProvider,
	SmartLink3PProjectContext,
} from '@atlaskit/rovo-triggers/types';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';

import { EMPTY_CONVERSATION_ID } from '../chat';

import type { FeatureSource } from './feature-source';

export type Menu =
	| 'conversation-list'
	| 'agents'
	| 'pulse'
	| 'tasks'
	| 'library'
	| 'scheduled'
	| 'search-work'
	| 'recommended-spaces'
	| 'browse-spaces'
	| 'space-landing'
	| 'remix'
	| 'none';

/**
 * A minimal space shape stored in ui-store. Structurally compatible with the full
 * `Space` type from `@atlassian/rovo-spaces/controllers/space-store/types` but kept separate here
 * to avoid a circular dependency (rovo-navigation already depends on this package).
 *
 * TODO (ROVOFE-2740): Move this to a shared `@atlassian/rovo-spaces-types` package so
 * rovo-spaces, rovo-navigation and conversation-assistant-store all import from one place.
 */
export type SelectedSpaceSource = {
	ari: string;
	url: string;
	sourceType: string;
	name?: string | null;
};

export type SelectedSpace = {
	id: string;
	name: string;
	emoji?: string | null;
	sources?: SelectedSpaceSource[];
	entryPoint?: RovoSpaceEntryPoint;
	isFirstSpaceOnboarding?: boolean;
};

/**
 * A non-space item (a pinned Jira Issue, Atlas Goal, or Atlas Project) that an empty-chat
 * launch is scoped to. Mutually exclusive with `selectedSpace` — a launch is either for a
 * real space (which gets the space pill + space-landing navigation) or for one of these.
 * `entityType` mirrors the backend's own enum values, unlike rovo-pins' local `PinType`.
 */
export type SelectedPinnedItem = {
	entityType: 'AtlasGoal' | 'AtlasProject' | 'JiraIssue';
	ari: string;
	name: string;
	url: string;
};

export type ConversationHeaderState = {
	conversationId: string;
	/**
	 * Metadata for the sticky conversation title block, not the main chat header.
	 * Currently used by the Insights preview flow.
	 */
	title?: string;
	icon?: ComponentType<NewCoreIconProps>;
	iconAppearance?: IconTileProps['appearance'];
	/**
	 * `'pulse'` marks an open Rovo Insights detail view. The header reads this alongside
	 * `activeMenu` from the same store snapshot so its "Insights" title can't tear during the
	 * feed⇄detail transition.
	 */
	source?: FeatureSource;
} | null;

/**
 * A serializable action descriptor stored in the UI store for empty-chat launches.
 * Uses string literals instead of importing from @atlassian/rovo-conversation-actions
 * to avoid a circular dependency:
 *   conversation-assistant-store → rovo-conversation-actions → conversation-assistant-store
 *
 * String values correspond to:
 *   type: AIActionType from @atlassian/rovo-conversation-actions/types
 *   handleType: HandleActionType enum from @atlassian/rovo-conversation-actions/types
 */
export type EmptyChatLaunchAction = {
	id: string;
	/** @see AIActionType in @atlassian/rovo-conversation-actions/types */
	type: 'ai-action' | 'agent-action' | 'convo-starter-action' | 'skill-action';
	/** @see HandleActionType in @atlassian/rovo-conversation-actions/types */
	handleType: 'Base' | 'Chat' | 'Nested' | 'Skill';
	staticPrompt?: string;
	staticMessage?: string;
	title?: string;
	icon?: () => React.JSX.Element;
};

export type EmptyChatLaunchGreeting = {
	iconSrc?: string;
	iconSrcDark?: string;
	title?: string;
	titleSize?: HeadingProps['size'];
	subtitle?: string;
	testId?: string;
	animationDuration?: 'small' | 'medium' | 'large';
	entranceDirection?: 'top' | 'bottom' | 'left' | 'right';
	exitDirection?: 'top' | 'bottom' | 'left' | 'right';
	animationKey?: string;
	truncateGreeting?: boolean;
	imageHeight?: number;
};

/** Project context for empty-chat launches. Mapped to the generic entityContext
 * shape at the display layer (ContextBanner) for entity-agnostic rendering. */
export type EmptyChatLaunchProjectContext = {
	/** Used in the backend sendMessage payload, not for display */
	projectId: string;
	projectName: string;
	projectUrl: string;
	/** When set, passed directly as space_id in sendMessageStream to scope RAG to the space's connected sources. */
	spaceId?: string;
};

export type SmartLink3PPostAuthState = {
	isActive: boolean;
	extensionKey?: string;
	provider?: SmartLink3PPostAuthProvider;
	projectContext?: SmartLink3PProjectContext;
};

export const defaultSmartLink3PPostAuthState: SmartLink3PPostAuthState = {
	isActive: false,
};

type State = {
	activeMenu: Menu;
	mounted: boolean;
	isAgentSelectorMenuOpen: boolean;
	streaming: boolean;
	isChatOnboardingMiniModalOpen: boolean;
	conversationHeader?: ConversationHeaderState;
	emptyChatLaunchGreeting?: EmptyChatLaunchGreeting;
	emptyChatLaunchProjectContext?: EmptyChatLaunchProjectContext;
	emptyChatLaunchInteractionSource?: InteractionSource;
	emptyChatLaunchInvokedFrom?: string;
	emptyChatLaunchConversationActions?: EmptyChatLaunchAction[];
	emptyChatLaunchReplaceProviderActions: boolean;
	emptyChatLaunchSkillRegistryTags?: string[];
	emptyChatLaunchId: number;
	// True while an empty-chat launch is active, independent of whether a custom greeting is set.
	// Consumers use this (not greeting presence) to decide "are we in a launched empty chat?".
	emptyChatLaunchActive: boolean;
	selectedSpace: SelectedSpace | undefined;
	selectedPinnedItem: SelectedPinnedItem | undefined;
	activeTabId: string;
	previousTabId: string;
	lastTabConversationId: string;
	lastLifecycleConversationId: string;
	lastLifecycleLaunchId: number | undefined;
	smartLink3PPostAuth: SmartLink3PPostAuthState;
	shouldFocusChatSearch: boolean;
};

const initialState: State = {
	activeMenu: 'none',
	mounted: false,
	isAgentSelectorMenuOpen: false,
	streaming: false,
	isChatOnboardingMiniModalOpen: false, // If productionized will look to move this logic into conversation-assistant-onboarding
	emptyChatLaunchGreeting: undefined,
	emptyChatLaunchProjectContext: undefined,
	emptyChatLaunchInteractionSource: undefined,
	emptyChatLaunchInvokedFrom: undefined,
	emptyChatLaunchConversationActions: undefined,
	emptyChatLaunchReplaceProviderActions: false,
	emptyChatLaunchSkillRegistryTags: undefined,
	emptyChatLaunchId: 0,
	emptyChatLaunchActive: false,
	selectedSpace: undefined,
	selectedPinnedItem: undefined,
	activeTabId: 'suggestions',
	previousTabId: 'suggestions',
	lastTabConversationId: '',
	lastLifecycleConversationId: EMPTY_CONVERSATION_ID,
	lastLifecycleLaunchId: undefined,
	smartLink3PPostAuth: defaultSmartLink3PPostAuthState,
	shouldFocusChatSearch: false,
};

const store = createStore({
	name: 'uiStore',
	initialState,
	actions: {
		// created for resetting store during tests
		reset:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({
					...initialState,
					conversationHeader: null,
					selectedSpace: undefined,
				});
			},
		setActiveMenu:
			(menu: Menu): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ activeMenu: menu });
			},
		setMounted:
			(mounted: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ mounted });
			},
		setIsAgentSelectorMenuOpen:
			(isOpen: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ isAgentSelectorMenuOpen: isOpen });
			},
		setStreamingStatus:
			(status: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ streaming: status });
			},
		setIsChatOnboardingMiniModalOpen:
			(status: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ isChatOnboardingMiniModalOpen: status });
			},
		setSmartLink3PPostAuth:
			(payload: SmartLink3PPostAuthState): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ smartLink3PPostAuth: payload });
			},
		clearSmartLink3PPostAuth:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ smartLink3PPostAuth: defaultSmartLink3PPostAuthState });
			},
		setConversationHeader:
			(header: ConversationHeaderState): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ conversationHeader: header });
			},
		getIsMounted:
			(): (({ getState }: StoreActionApi<State>) => boolean) =>
			({ getState }): boolean => {
				return getState().mounted;
			},
		setEmptyChatLaunch:
			(payload: {
				customGreeting?: EmptyChatLaunchGreeting;
				customConversationActions?: EmptyChatLaunchAction[];
				projectContext?: EmptyChatLaunchProjectContext;
				interactionSource?: InteractionSource;
				invokedFrom?: string;
				/**
				 * When true, customConversationActions fully replace the prop-based getActions function.
				 * This takes precedence over the consumer's prioritizeCustomActionResults prop —
				 * even if prioritizeCustomActionResults=true is set statically, the store actions win
				 * because mergedGetActions returns only the store actions before the display layer is reached.
				 */
				replaceProviderActions?: boolean;

				/**
				 * When provided, these tags are used to fetch skill metadata for the empty state actions.
				 * Only set this when the launch source needs skill hover cards (e.g. rovo-for-you project actions).
				 * If not provided, no additional skill fetch is triggered.
				 */
				skillRegistryTags?: string[];
			}): (({ setState, getState }: StoreActionApi<State>) => void) =>
			({ setState, getState }) => {
				setState({
					emptyChatLaunchGreeting: payload.customGreeting,
					emptyChatLaunchConversationActions: payload.customConversationActions,
					emptyChatLaunchProjectContext: payload.projectContext,
					emptyChatLaunchInteractionSource: payload.interactionSource,
					emptyChatLaunchInvokedFrom: payload.invokedFrom,
					emptyChatLaunchReplaceProviderActions: payload.replaceProviderActions ?? false,
					emptyChatLaunchSkillRegistryTags: payload.skillRegistryTags,
					emptyChatLaunchId: getState().emptyChatLaunchId + 1,
					emptyChatLaunchActive: true,
				});
			},
		clearEmptyChatLaunchGreeting:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({
					emptyChatLaunchGreeting: undefined,
					emptyChatLaunchConversationActions: undefined,
					emptyChatLaunchInteractionSource: undefined,
					emptyChatLaunchInvokedFrom: undefined,
					emptyChatLaunchReplaceProviderActions: false,
					emptyChatLaunchSkillRegistryTags: undefined,
				});
			},
		clearEmptyChatLaunchAll:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({
					emptyChatLaunchGreeting: undefined,
					emptyChatLaunchConversationActions: undefined,
					emptyChatLaunchProjectContext: undefined,
					emptyChatLaunchInteractionSource: undefined,
					emptyChatLaunchInvokedFrom: undefined,
					emptyChatLaunchReplaceProviderActions: false,
					emptyChatLaunchSkillRegistryTags: undefined,
					emptyChatLaunchActive: false,
				});
			},
		setShouldFocusChatSearch:
			(shouldFocus: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ shouldFocusChatSearch: shouldFocus });
			},
		setSelectedSpace:
			(space: SelectedSpace | undefined): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ selectedSpace: space });
			},
		clearSelectedSpace:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ selectedSpace: undefined });
			},
		setSelectedPinnedItem:
			(item: SelectedPinnedItem | undefined): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ selectedPinnedItem: item });
			},
		clearSelectedPinnedItem:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ selectedPinnedItem: undefined });
			},
		setActiveTabId:
			(tabId: string): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ activeTabId: tabId });
			},
		setPreviousTabId:
			(tabId: string): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ previousTabId: tabId });
			},
		setLastTabConversationId:
			(id: string): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ lastTabConversationId: id });
			},
		checkAndUpdateLifecycleGuard:
			(
				conversationId: string,
				launchId: number | undefined,
			): (({ getState, setState }: StoreActionApi<State>) => boolean) =>
			({ getState, setState }): boolean => {
				const { lastLifecycleConversationId, lastLifecycleLaunchId } = getState();
				if (lastLifecycleConversationId === conversationId && lastLifecycleLaunchId === launchId) {
					return false;
				}
				setState({
					lastLifecycleConversationId: conversationId,
					lastLifecycleLaunchId: launchId,
				});
				return true;
			},
	},
});

const useStore = createHook(store);

export const useEmptyChatLaunchSelector: HookFunction<
	{
		customGreeting: EmptyChatLaunchGreeting | undefined;
		projectContext: EmptyChatLaunchProjectContext | undefined;
		interactionSource: InteractionSource | undefined;
		invokedFrom: string | undefined;
		customConversationActions: EmptyChatLaunchAction[] | undefined;
		replaceProviderActions: boolean;
		skillRegistryTags: string[] | undefined;
		launchId: number;
		isEmptyChatLaunchActive: boolean;
	},
	BoundActions<
		State,
		{
			// created for resetting store during tests
			reset: () => ({ setState }: StoreActionApi<State>) => void;
			setActiveMenu: (menu: Menu) => ({ setState }: StoreActionApi<State>) => void;
			setMounted: (mounted: boolean) => ({ setState }: StoreActionApi<State>) => void;
			setIsAgentSelectorMenuOpen: (
				isOpen: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setStreamingStatus: (status: boolean) => ({ setState }: StoreActionApi<State>) => void;
			setIsChatOnboardingMiniModalOpen: (
				status: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setSmartLink3PPostAuth: (
				payload: SmartLink3PPostAuthState,
			) => ({ setState }: StoreActionApi<State>) => void;
			clearSmartLink3PPostAuth: () => ({ setState }: StoreActionApi<State>) => void;
			setConversationHeader: (
				header: ConversationHeaderState,
			) => ({ setState }: StoreActionApi<State>) => void;
			getIsMounted: () => ({ getState }: StoreActionApi<State>) => boolean;
			setEmptyChatLaunch: (payload: {
				customGreeting?: EmptyChatLaunchGreeting;
				customConversationActions?: EmptyChatLaunchAction[];
				projectContext?: EmptyChatLaunchProjectContext;
				interactionSource?: InteractionSource;
				invokedFrom?: string;
				/**
				 * When true, customConversationActions fully replace the prop-based getActions function.
				 * This takes precedence over the consumer's prioritizeCustomActionResults prop —
				 * even if prioritizeCustomActionResults=true is set statically, the store actions win
				 * because mergedGetActions returns only the store actions before the display layer is reached.
				 */
				replaceProviderActions?: boolean;
				skillRegistryTags?: string[];
			}) => ({ setState, getState }: StoreActionApi<State>) => void;
			clearEmptyChatLaunchGreeting: () => ({ setState }: StoreActionApi<State>) => void;
			clearEmptyChatLaunchAll: () => ({ setState }: StoreActionApi<State>) => void;
			setActiveTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setPreviousTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setLastTabConversationId: (id: string) => ({ setState }: StoreActionApi<State>) => void;
			checkAndUpdateLifecycleGuard: (
				conversationId: string,
				launchId: number | undefined,
			) => ({ getState, setState }: StoreActionApi<State>) => boolean;
		}
	>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		customGreeting: state.emptyChatLaunchGreeting,
		projectContext: state.emptyChatLaunchProjectContext,
		interactionSource: state.emptyChatLaunchInteractionSource,
		invokedFrom: state.emptyChatLaunchInvokedFrom,
		customConversationActions: state.emptyChatLaunchConversationActions,
		replaceProviderActions: state.emptyChatLaunchReplaceProviderActions,
		skillRegistryTags: state.emptyChatLaunchSkillRegistryTags,
		launchId: state.emptyChatLaunchId,
		isEmptyChatLaunchActive: state.emptyChatLaunchActive,
	}),
});

export const useActiveTabSelector: HookFunction<
	{
		activeTabId: string;
		previousTabId: string;
		lastTabConversationId: string;
	},
	BoundActions<
		State,
		{
			setActiveTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setPreviousTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setLastTabConversationId: (id: string) => ({ setState }: StoreActionApi<State>) => void;
		}
	>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		activeTabId: state.activeTabId,
		previousTabId: state.previousTabId,
		lastTabConversationId: state.lastTabConversationId,
	}),
});

/**
 * Narrow selector for consumers that only need `selectedSpace` (e.g. ChatHistory).
 * Subscribing through this hook re-renders the consumer only when `selectedSpace`
 * changes, instead of on every UI-store state change as the broad `useUIStore`
 * does. Actions are still available via `useUIStoreActions`.
 */
export const useSelectedSpaceSelector: HookFunction<
	{
		selectedSpace: SelectedSpace | undefined;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		selectedSpace: state.selectedSpace,
	}),
});

/**
 * Narrow selector for consumers that only need `selectedPinnedItem` (e.g. the
 * chat-input pinned-entity bar). Subscribing through this hook re-renders the
 * consumer only when `selectedPinnedItem` changes. Actions are still available
 * via `useUIStoreActions`.
 */
export const useSelectedPinnedItemSelector: HookFunction<
	{
		selectedPinnedItem: SelectedPinnedItem | undefined;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		selectedPinnedItem: state.selectedPinnedItem,
	}),
});

/**
 * Narrow selector for the chat main header, which reads only these four UI-store
 * fields. Subscribing through this hook re-renders the header only when one of
 * these fields changes, instead of on every UI-store state change. Actions are
 * still available via `useUIStoreActions`.
 */
export const useChatHeaderUISelector: HookFunction<
	{
		activeMenu: Menu;
		isAgentSelectorMenuOpen: boolean;
		isChatOnboardingMiniModalOpen: boolean;
		selectedSpace: SelectedSpace | undefined;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		activeMenu: state.activeMenu,
		isAgentSelectorMenuOpen: state.isAgentSelectorMenuOpen,
		isChatOnboardingMiniModalOpen: state.isChatOnboardingMiniModalOpen,
		selectedSpace: state.selectedSpace,
	}),
});

/**
 * Narrow selector for consumers that only need `activeMenu`. Subscribing through
 * this hook re-renders the consumer only when `activeMenu` changes, instead of on
 * every UI-store state change as the broad `useUIStore` does. Actions are still
 * available via `useUIStoreActions`.
 */
export const useActiveMenuSelector: HookFunction<
	{
		activeMenu: Menu;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		activeMenu: state.activeMenu,
	}),
});

/**
 * Narrow selector for consumers that only need `mounted`. Subscribing through this
 * hook re-renders the consumer only when `mounted` changes, instead of on every
 * UI-store state change. Actions are still available via `useUIStoreActions`.
 */
export const useMountedSelector: HookFunction<
	{
		mounted: boolean;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		mounted: state.mounted,
	}),
});

export const useShouldFocusChatSearchSelector: HookFunction<
	{
		shouldFocusChatSearch: boolean;
	},
	BoundActions<
		State,
		{
			setShouldFocusChatSearch: (
				shouldFocus: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
		}
	>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		shouldFocusChatSearch: state.shouldFocusChatSearch,
	}),
});

/**
 * Narrow selector for consumers that only need `isChatOnboardingMiniModalOpen`.
 * Subscribing through this hook re-renders the consumer only when that flag
 * changes, instead of on every UI-store state change. Actions are still available
 * via `useUIStoreActions`.
 */
export const useOnboardingModalSelector: HookFunction<
	{
		isChatOnboardingMiniModalOpen: boolean;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		isChatOnboardingMiniModalOpen: state.isChatOnboardingMiniModalOpen,
	}),
});

/**
 * Narrow selector for consumers that only need `conversationHeader`. Subscribing
 * through this hook re-renders the consumer only when `conversationHeader` changes,
 * instead of on every UI-store state change. Actions are still available via
 * `useUIStoreActions`.
 */
export const useConversationHeaderSelector: HookFunction<
	{
		conversationHeader: ConversationHeaderState | undefined;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		conversationHeader: state.conversationHeader,
	}),
});

/**
 * Narrow selector for consumers that only need `smartLink3PPostAuth`. Subscribing
 * through this hook re-renders the consumer only when `smartLink3PPostAuth`
 * changes, instead of on every UI-store state change. Actions are still available
 * via `useUIStoreActions`.
 */
export const useSmartLink3PSelector: HookFunction<
	{
		smartLink3PPostAuth: SmartLink3PPostAuthState;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		smartLink3PPostAuth: state.smartLink3PPostAuth,
	}),
});

/**
 * Composite narrow selector for the chat input, which reads `selectedSpace`,
 * `isChatOnboardingMiniModalOpen` and `smartLink3PPostAuth`. Subscribing through
 * this hook re-renders the consumer only when one of those fields changes, instead
 * of on every UI-store state change. Actions are still available via
 * `useUIStoreActions`.
 */
export const useChatInputUISelector: HookFunction<
	{
		selectedSpace: State['selectedSpace'];
		isChatOnboardingMiniModalOpen: boolean;
		smartLink3PPostAuth: SmartLink3PPostAuthState;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		selectedSpace: state.selectedSpace,
		isChatOnboardingMiniModalOpen: state.isChatOnboardingMiniModalOpen,
		smartLink3PPostAuth: state.smartLink3PPostAuth,
	}),
});

/**
 * Composite narrow selector for surfaces that need `activeMenu` and
 * `selectedSpace` together (e.g. the side navigation). Subscribing through this
 * hook re-renders the consumer only when one of those two fields changes, instead
 * of on every UI-store state change. Actions are still available via
 * `useUIStoreActions`.
 */
export const useSideNavUISelector: HookFunction<
	{
		activeMenu: Menu;
		selectedSpace: State['selectedSpace'];
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		activeMenu: state.activeMenu,
		selectedSpace: state.selectedSpace,
	}),
});

/**
 * Composite narrow selector for the root ConversationAssistantRender segment,
 * which reads `activeMenu`, `selectedSpace`, `isChatOnboardingMiniModalOpen` and
 * `smartLink3PPostAuth`. Subscribing through this hook re-renders the root only
 * when one of those fields changes, instead of on every UI-store state change.
 * Actions are still available via `useUIStoreActions`.
 */
export const useRootUISelector: HookFunction<
	{
		activeMenu: Menu;
		selectedSpace: State['selectedSpace'];
		isChatOnboardingMiniModalOpen: boolean;
		smartLink3PPostAuth: SmartLink3PPostAuthState;
	},
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		activeMenu: state.activeMenu,
		selectedSpace: state.selectedSpace,
		isChatOnboardingMiniModalOpen: state.isChatOnboardingMiniModalOpen,
		smartLink3PPostAuth: state.smartLink3PPostAuth,
	}),
});

export const useUIStore = (): {
	activeMenu: Menu;
	mounted: boolean;
	streaming: boolean;
	isAgentSelectorMenuOpen: boolean;
	conversationHeader?: ConversationHeaderState;
	selectedSpace: SelectedSpace | undefined;
	selectedPinnedItem: SelectedPinnedItem | undefined;
	reset: () => void;
	setActiveMenu: (menu: Menu) => void;
	setMounted: (mounted: boolean) => void;
	setIsAgentSelectorMenuOpen: (isOpen: boolean) => void;
	setStreamingStatus: (status: boolean) => void;
	isChatOnboardingMiniModalOpen: boolean;
	setIsChatOnboardingMiniModalOpen: (status: boolean) => void;
	setConversationHeader: (header: ConversationHeaderState) => void;
	setSelectedSpace: (space: SelectedSpace | undefined) => void;
	clearSelectedSpace: () => void;
	setSelectedPinnedItem: (item: SelectedPinnedItem | undefined) => void;
	clearSelectedPinnedItem: () => void;
	activeTabId: string;
	previousTabId: string;
	lastTabConversationId: string;
	smartLink3PPostAuth: SmartLink3PPostAuthState;
	setSmartLink3PPostAuth: (payload: SmartLink3PPostAuthState) => void;
	clearSmartLink3PPostAuth: () => void;
	setActiveTabId: (tabId: string) => void;
	setPreviousTabId: (tabId: string) => void;
	setLastTabConversationId: (id: string) => void;
} => {
	const [
		{
			activeMenu,
			mounted,
			streaming,
			isAgentSelectorMenuOpen,
			isChatOnboardingMiniModalOpen,
			conversationHeader,
			selectedSpace,
			selectedPinnedItem,
			activeTabId,
			previousTabId,
			lastTabConversationId,
			smartLink3PPostAuth,
		},
		{
			reset,
			setActiveMenu,
			setMounted,
			setIsAgentSelectorMenuOpen,
			setStreamingStatus,
			setIsChatOnboardingMiniModalOpen,
			setConversationHeader,
			setSelectedSpace,
			clearSelectedSpace,
			setSelectedPinnedItem,
			clearSelectedPinnedItem,
			setActiveTabId,
			setPreviousTabId,
			setLastTabConversationId,
			setSmartLink3PPostAuth,
			clearSmartLink3PPostAuth,
		},
	] = useStore();

	return {
		activeMenu,
		mounted,
		streaming,
		isAgentSelectorMenuOpen,
		conversationHeader: conversationHeader ?? null,
		selectedSpace,
		selectedPinnedItem,
		reset,
		setActiveMenu,
		setMounted,
		setIsAgentSelectorMenuOpen,
		setStreamingStatus,
		isChatOnboardingMiniModalOpen,
		setIsChatOnboardingMiniModalOpen,
		setConversationHeader,
		setSelectedSpace,
		clearSelectedSpace,
		setSelectedPinnedItem,
		clearSelectedPinnedItem,
		activeTabId,
		previousTabId,
		lastTabConversationId,
		smartLink3PPostAuth,
		setSmartLink3PPostAuth,
		clearSmartLink3PPostAuth,
		setActiveTabId,
		setPreviousTabId,
		setLastTabConversationId,
	};
};

export const useUIStoreActions: HookActionsFunction<
	BoundActions<
		State,
		{
			// created for resetting store during tests
			reset: () => ({ setState }: StoreActionApi<State>) => void;
			setActiveMenu: (menu: Menu) => ({ setState }: StoreActionApi<State>) => void;
			setMounted: (mounted: boolean) => ({ setState }: StoreActionApi<State>) => void;
			setIsAgentSelectorMenuOpen: (
				isOpen: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setStreamingStatus: (status: boolean) => ({ setState }: StoreActionApi<State>) => void;
			setIsChatOnboardingMiniModalOpen: (
				status: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setSmartLink3PPostAuth: (
				payload: SmartLink3PPostAuthState,
			) => ({ setState }: StoreActionApi<State>) => void;
			clearSmartLink3PPostAuth: () => ({ setState }: StoreActionApi<State>) => void;
			setConversationHeader: (
				header: ConversationHeaderState,
			) => ({ setState }: StoreActionApi<State>) => void;
			getIsMounted: () => ({ getState }: StoreActionApi<State>) => boolean;
			setEmptyChatLaunch: (payload: {
				customGreeting?: EmptyChatLaunchGreeting;
				customConversationActions?: EmptyChatLaunchAction[];
				projectContext?: EmptyChatLaunchProjectContext;
				interactionSource?: InteractionSource;
				invokedFrom?: string;
				replaceProviderActions?: boolean;
				skillRegistryTags?: string[];
			}) => ({ setState, getState }: StoreActionApi<State>) => void;
			clearEmptyChatLaunchGreeting: () => ({ setState }: StoreActionApi<State>) => void;
			clearEmptyChatLaunchAll: () => ({ setState }: StoreActionApi<State>) => void;
			setShouldFocusChatSearch: (
				shouldFocus: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setSelectedSpace: (
				space: SelectedSpace | undefined,
			) => ({ setState }: StoreActionApi<State>) => void;
			clearSelectedSpace: () => ({ setState }: StoreActionApi<State>) => void;
			setSelectedPinnedItem: (
				item: SelectedPinnedItem | undefined,
			) => ({ setState }: StoreActionApi<State>) => void;
			clearSelectedPinnedItem: () => ({ setState }: StoreActionApi<State>) => void;
			setActiveTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setPreviousTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setLastTabConversationId: (id: string) => ({ setState }: StoreActionApi<State>) => void;
			checkAndUpdateLifecycleGuard: (
				conversationId: string,
				launchId: number | undefined,
			) => ({ getState, setState }: StoreActionApi<State>) => boolean;
		}
	>
> = createActionsHook(store);

const isRenderStabilizationEnabled = (): boolean =>
	FeatureGates.getExperimentValue('rovo_ttai_segment_render_stabilization', 'isEnabled', false);

/**
 * Subscription for action-only consumers, called alongside `useUIStoreActions()` (the
 * return value is unused). While the render-stabilization experiment is off it subscribes
 * to the whole UI store, matching the old broad `useUIStore()` re-render behaviour; while
 * on it subscribes to nothing, so the consumer stops re-rendering on UI-store changes.
 */
export const useUIStoreSubscription: HookFunction<
	State | null,
	BoundActions<State, Record<never, never>>,
	void
> = createHook(store, {
	selector: (state: State): State | null => (isRenderStabilizationEnabled() ? null : state),
});
